import { useState, useEffect, forwardRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2, Edit, Link2 } from 'lucide-react';
import type { Chart as DBChart, ChartDataPoint } from '@shared/schemas/chart';
import SlidingToggle from '@/components/ui/SlidingToggle';
import { ChartService } from '../../services/chartService';
import { useAuth } from '../../contexts/AuthContext';
import { useAtom } from 'jotai';
import { currentOrganizationAtom } from '../../store/organizationAtoms';
import DataSourceSelector from '../datasources/DataSourceSelector';
import { useToast } from '@/hooks/use-toast';

type EditMode = 'manual' | 'dataSource';

interface ChartDataEditorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  chart: DBChart | null;
  onChartUpdate: () => void;
}

const ChartDataEditorDialog = forwardRef<HTMLDivElement, ChartDataEditorDialogProps>((
  {
    isOpen,
    onClose,
    chart,
    onChartUpdate,
  },
  _ref
) => {
  const [editMode, setEditMode] = useState<EditMode>('manual');
  const [manualData, setManualData] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dataSourceData, setDataSourceData] = useState<any[] | null>(null);
  const [selectedCollection, setSelectedCollection] = useState<string | null>(null);
  const [selectedDataSource, setSelectedDataSource] = useState<string | null>(null);

  const { currentUser } = useAuth();
  const [currentOrganization] = useAtom(currentOrganizationAtom);
  const { toast } = useToast();

  const toggleOptions = [
    { value: 'manual', label: 'Manual Edit', icon: <Edit className="h-4 w-4" /> },
    { value: 'dataSource', label: 'Data Source', icon: <Link2 className="h-4 w-4" /> },
  ];

  useEffect(() => {
    if (chart && chart.datasets && chart.datasets.length > 0 && chart.datasets[0].data) {
      try {
        const currentData = chart.datasets[0].data.map(dp => ({ label: dp.label, value: dp.value }));
        setManualData(JSON.stringify(currentData, null, 2));
      } catch (e) {
        setManualData('Error parsing current data');
        console.error("Error stringifying chart data:", e);
      }
    } else {
      setManualData('[]');
    }
    setError(null);
  }, [chart]);

  // Analyze Firestore data and convert to chart format
  const analyzeFirestoreData = (data: any[]): ChartDataPoint[] => {
    if (!data || data.length === 0) return [];

    // Get all available fields from the first few documents
    const sampleSize = Math.min(5, data.length);
    const allFields = new Set<string>();
    
    for (let i = 0; i < sampleSize; i++) {
      Object.keys(data[i] || {}).forEach(key => {
        if (key !== 'id') { // Exclude the document ID
          allFields.add(key);
        }
      });
    }

    const fields = Array.from(allFields);
    
    // Find the best fields for charting
    const numericFields = fields.filter(field => 
      data.every(row => {
        const value = row[field];
        return value !== null && value !== undefined && !isNaN(Number(value));
      })
    );
    
    const textFields = fields.filter(field => 
      !numericFields.includes(field) && 
      data.every(row => {
        const value = row[field];
        return typeof value === 'string' && value.length > 0;
      })
    );

    // Choose the best label and value fields
    let labelField = textFields[0] || fields[0] || 'id';
    let valueField = numericFields[0] || fields.find(f => f !== labelField) || fields[0];

    // If we don't have a good numeric field, try to convert the first non-label field
    if (!numericFields.length && fields.length > 1) {
      valueField = fields.find(f => f !== labelField) || fields[0];
    }

    // Convert to chart data points
    return data.map((row, index) => {
      const label = row[labelField] ? String(row[labelField]) : `Item ${index + 1}`;
      let value: number;
      
      if (numericFields.includes(valueField)) {
        value = Number(row[valueField]);
      } else if (row[valueField] !== undefined && row[valueField] !== null) {
        // Try to convert to number, fallback to length or 1
        const converted = Number(row[valueField]);
        if (!isNaN(converted)) {
          value = converted;
        } else if (typeof row[valueField] === 'string') {
          value = row[valueField].length;
        } else {
          value = 1;
        }
      } else {
        value = 1;
      }
      
      return { label, value };
    });
  };

  const handleDataSourceSelection = (data: any[], collectionName: string, dataSourceName: string) => {
    setDataSourceData(data);
    setSelectedCollection(collectionName);
    setSelectedDataSource(dataSourceName);
    setError(null);

    // Analyze the data and show a preview
    const chartData = analyzeFirestoreData(data);
    
    toast({
      title: "Data Loaded",
      description: `Loaded ${chartData.length} data points from "${collectionName}". Ready to update chart.`,
      variant: "success",
    });
  };

  const handleSave = async () => {
    console.log('Attempting to save chart data...');
    console.log('Current User:', currentUser);
    console.log('Current Organization:', currentOrganization);
    console.log('Chart Object:', chart);

    if (!chart || !chart.id || !currentUser || !currentOrganization || !chart.userId) {
      const missingParts = [];
      if (!chart || !chart.id) missingParts.push('chart details');
      if (!chart?.userId) missingParts.push('chart.userId');
      if (!currentUser) missingParts.push('current user');
      if (!currentOrganization) missingParts.push('current organization');
      const errorMessage = `Save failed: Missing ${missingParts.join(', ')}. Cannot save.`;
      console.error(errorMessage);
      setError(errorMessage);
      return;
    }

    setIsSaving(true);
    setError(null);

    if (editMode === 'manual') {
      try {
        const parsedData: Array<{ label: string; value: number }> = JSON.parse(manualData);
        if (!Array.isArray(parsedData) || !parsedData.every(item => typeof item.label === 'string' && typeof item.value === 'number')) {
          throw new Error('Invalid data format. Expecting an array of {label: string, value: number} objects.');
        }
        const chartDataPoints: ChartDataPoint[] = parsedData.map((item) => ({
          label: item.label,
          value: item.value,
        }));

        const updatedDatasets = [
          {
            ...(chart.datasets && chart.datasets.length > 0 ? chart.datasets[0] : { label: 'Dataset 1' }),
            data: chartDataPoints,
          },
        ];
        
        console.log('Calling ChartService.update with:', {
          orgId: currentOrganization.id,
          userId: chart.userId,
          chartId: chart.id,
          payload: { datasets: updatedDatasets }
        });

        await ChartService.update(currentOrganization.id, chart.userId, chart.id, { datasets: updatedDatasets });
        
        console.log('Chart data saved successfully.');
        onChartUpdate();
        onClose();
      } catch (e: any) {
        console.error('Error saving chart data:', e);
        setError(e.message || 'Failed to parse or save data. Please ensure it is valid JSON.');
      }
    } else if (editMode === 'dataSource') {
      if (!dataSourceData || dataSourceData.length === 0) {
        setError('Please select a data source and collection first.');
        setIsSaving(false);
        return;
      }

      try {
        const chartDataPoints = analyzeFirestoreData(dataSourceData);
        
        if (chartDataPoints.length === 0) {
          throw new Error('No valid data points could be extracted from the selected collection.');
        }

        const updatedDatasets = [
          {
            ...(chart.datasets && chart.datasets.length > 0 ? chart.datasets[0] : { label: 'Dataset 1' }),
            data: chartDataPoints,
          },
        ];
        
        console.log('Updating chart with data source data:', {
          orgId: currentOrganization.id,
          userId: chart.userId,
          chartId: chart.id,
          payload: { datasets: updatedDatasets },
          source: `${selectedDataSource} - ${selectedCollection}`
        });

        await ChartService.update(currentOrganization.id, chart.userId, chart.id, { datasets: updatedDatasets });
        
        console.log('Chart data updated successfully from data source.');
        toast({
          title: "Chart Updated",
          description: `Chart updated with ${chartDataPoints.length} data points from "${selectedCollection}".`,
          variant: "success",
        });
        
        onChartUpdate();
        onClose();
      } catch (e: any) {
        console.error('Error updating chart with data source:', e);
        setError(e.message || 'Failed to update chart with data source data.');
      }
    }
    setIsSaving(false);
  };

  if (!isOpen || !chart) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="sm:max-w-md md:sm:max-w-lg lg:md:max-w-xl"
        onPointerDownCapture={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>Edit Chart Data: {chart.title}</DialogTitle>
          <DialogDescription>
            Modify the chart data manually or change the data source.
          </DialogDescription>
        </DialogHeader>

        <div className="my-4 flex justify-center">
          <SlidingToggle
            options={toggleOptions}
            value={editMode}
            onChange={(value) => {
              setEditMode(value as EditMode);
              setError(null);
            }}
            textSize="sm"
            className="w-full max-w-xs"
          />
        </div>

        {editMode === 'manual' && (
          <div className="space-y-3 py-2">
            <Label htmlFor="manual-data-input">{`Chart Data (JSON: [{"label": "A", "value": 10}, ...])`}</Label>
            <Textarea
              id="manual-data-input"
              value={manualData}
              onChange={(e) => setManualData(e.target.value)}
              rows={10}
              placeholder={`[{"label": "Category A", "value": 100}, {"label": "Category B", "value": 150}]`}
              className="focus:ring-orange-500 focus:border-orange-500 dark:focus:ring-orange-400 dark:focus:border-orange-400 caret-orange-500 dark:caret-orange-400"
            />
            <p className="text-xs text-muted-foreground">
              Enter data as an array of objects, each with a 'label' (string) and 'value' (number).
            </p>
          </div>
        )}

        {editMode === 'dataSource' && (
          <div className="py-4 max-h-96 overflow-y-auto">
            {dataSourceData && selectedCollection ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <div>
                    <p className="text-sm font-medium text-green-800 dark:text-green-200">
                      Data Selected: {selectedCollection}
                    </p>
                    <p className="text-xs text-green-600 dark:text-green-400">
                      {dataSourceData.length} records from {selectedDataSource}
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setDataSourceData(null);
                      setSelectedCollection(null);
                      setSelectedDataSource(null);
                      setError(null);
                    }}
                  >
                    Change Data
                  </Button>
                </div>
                <div className="text-xs text-muted-foreground">
                  <p className="font-medium mb-1">Data Preview:</p>
                  <div className="bg-muted/50 p-2 rounded text-xs font-mono">
                    {JSON.stringify(dataSourceData.slice(0, 2), null, 2)}
                    {dataSourceData.length > 2 && '\n... and ' + (dataSourceData.length - 2) + ' more records'}
                  </div>
                </div>
              </div>
            ) : (
              <DataSourceSelector 
                onDataSelected={handleDataSourceSelection}
                onClose={onClose}
              />
            )}
          </div>
        )}

        {error && <p className="text-sm text-destructive py-2">{error}</p>}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || (editMode === 'dataSource' && (!dataSourceData || dataSourceData.length === 0))}>
            {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

ChartDataEditorDialog.displayName = 'ChartDataEditorDialog';

export default ChartDataEditorDialog; 